import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { GET_ROLE_PERMISSIONS } from "@src/domain/graphql";
import { GraphQlAdapter } from "@src/infrastructure/adapters/graphQl/GraphQl.adapter";
import type { FeaturePermission } from "@src/domain/dto/features";

export interface RolePermissionFeature {
  featureId: number;
  featureName: string;
  featureKey: string;
  featureDescription: string | null;
  featureIsActive: boolean;
  featureIsMainMenu: boolean;
  featureDisplayName: string | null;
  featurePermissions: FeaturePermission[];
}

export interface RolePermissionItem {
  roleId: number;
  roleName: string;
  roleKey: string;
  roleDescription: string | null;
  roleIsActive: boolean;
  allowedUserKinds: string[] | null;
  createdAt: string;
  updatedAt: string | null;
  roleFeatures: RolePermissionFeature[];
}

interface RolePermissionsResponse {
  rolePermissions: {
    count: number;
    items: RolePermissionItem[];
  };
}

interface ApplyRolePermissionUpdatePayload {
  roleId: number;
  featureId: number;
  isActive: boolean;
  permissionId?: number;
  featurePermissions?: FeaturePermission[];
  featureMeta?: RoleFeatureLookupMeta;
}

function buildFeaturePermissions(
  featurePermissions: FeaturePermission[] | undefined,
): FeaturePermission[] {
  return (featurePermissions ?? []).map((permission) => ({
    ...permission,
    isActive: true,
  }));
}

function buildRoleFeatureFromLookup(
  payload: ApplyRolePermissionUpdatePayload,
): RolePermissionFeature | null {
  if (!payload.featureMeta) return null;
  return {
    featureId: payload.featureId,
    featureName: payload.featureMeta.featureName,
    featureKey: payload.featureMeta.featureKey,
    featureDescription: payload.featureMeta.featureDescription,
    featureIsActive: true,
    featureIsMainMenu: payload.featureMeta.featureIsMainMenu,
    featureDisplayName: payload.featureMeta.featureDisplayName,
    featurePermissions: buildFeaturePermissions(payload.featurePermissions),
  };
}

function activateOrInsertPermission(
  feature: RolePermissionFeature,
  payload: ApplyRolePermissionUpdatePayload,
): RolePermissionFeature {
  const permissionId = payload.permissionId;
  if (permissionId == null) return feature;
  const existing = feature.featurePermissions.find(
    (permission) => permission.id === permissionId,
  );
  if (existing) {
    return {
      ...feature,
      featureIsActive: true,
      featurePermissions: feature.featurePermissions.map((permission) =>
        permission.id === permissionId ? { ...permission, isActive: true } : permission,
      ),
    };
  }
  const permissionToAdd = payload.featurePermissions?.find(
    (permission) => permission.id === permissionId,
  );
  if (!permissionToAdd) {
    return {
      ...feature,
      featureIsActive: true,
    };
  }
  return {
    ...feature,
    featureIsActive: true,
    featurePermissions: [...feature.featurePermissions, { ...permissionToAdd, isActive: true }],
  };
}

function updateFeatureByPayload(
  feature: RolePermissionFeature,
  payload: ApplyRolePermissionUpdatePayload,
): RolePermissionFeature {
  if (feature.featureId !== payload.featureId) return feature;

  if (payload.permissionId != null) {
    if (payload.isActive) {
      return activateOrInsertPermission(feature, payload);
    }
    return {
      ...feature,
      featurePermissions: feature.featurePermissions.filter(
        (permission) => permission.id !== payload.permissionId,
      ),
    };
  }

  if (payload.isActive) {
    return {
      ...feature,
      featureIsActive: true,
      featurePermissions:
        payload.featurePermissions?.map((permission) => ({
          ...permission,
          isActive: true,
        })) ?? feature.featurePermissions,
    };
  }

  return {
    ...feature,
    featureIsActive: false,
    featurePermissions: [],
  };
}

function updateRoleByPayload(
  role: RolePermissionItem,
  payload: ApplyRolePermissionUpdatePayload,
): RolePermissionItem {
  if (role.roleId !== payload.roleId) return role;

  const hasFeatureRow = role.roleFeatures.some((feature) => feature.featureId === payload.featureId);
  if (payload.permissionId == null && payload.isActive && !hasFeatureRow) {
    const featureFromLookup = buildRoleFeatureFromLookup(payload);
    if (!featureFromLookup) return role;
    return {
      ...role,
      roleFeatures: [...role.roleFeatures, featureFromLookup],
    };
  }

  return {
    ...role,
    roleFeatures: role.roleFeatures.map((feature) => updateFeatureByPayload(feature, payload)),
  };
}

/** Lookup fields needed when a role has no row yet for a feature (first-time enable). */
export interface RoleFeatureLookupMeta {
  featureName: string;
  featureKey: string;
  featureDescription: string | null;
  featureIsMainMenu: boolean;
  featureDisplayName: string | null;
}

export interface RolesPermissionsSlice {
  rolePermissions: RolePermissionItem[] | null;
  rolePermissionsCount: number;
  rolePermissionsLoading: boolean;
  rolePermissionsError: string | null;
  refetchRolePermissions: () => Promise<void>;
  applyRolePermissionUpdate: (payload: ApplyRolePermissionUpdatePayload) => void;
}

const RolesPermissionsContext = createContext<RolesPermissionsSlice | null>(null);

export function useRolesPermissions(): RolesPermissionsSlice {
  const value = useContext(RolesPermissionsContext);
  if (value === null) {
    throw new Error(
      "useRolesPermissions must be used within a RolesPermissionsProvider",
    );
  }
  return value;
}

interface RolesPermissionsProviderProps {
  children: ReactNode;
}

export function RolesPermissionsProvider({
  children,
}: Readonly<RolesPermissionsProviderProps>) {
  const [rolePermissions, setRolePermissions] = useState<RolePermissionItem[] | null>(
    null,
  );
  const [rolePermissionsCount, setRolePermissionsCount] = useState(0);
  const [rolePermissionsLoading, setRolePermissionsLoading] = useState(true);
  const [rolePermissionsError, setRolePermissionsError] = useState<string | null>(
    null,
  );

  const refetchRolePermissions = useCallback(async () => {
    setRolePermissionsLoading(true);
    setRolePermissionsError(null);
    try {
      const client = GraphQlAdapter.getInstance().getClient();
      const result = await client.query<RolePermissionsResponse>({
        query: GET_ROLE_PERMISSIONS,
      });
      const data = result.data?.rolePermissions;
      if (data) {
        setRolePermissions(data.items ?? null);
        setRolePermissionsCount(data.count ?? 0);
      } else {
        setRolePermissions(null);
        setRolePermissionsCount(0);
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load role permissions";
      setRolePermissionsError(message);
      setRolePermissions(null);
      setRolePermissionsCount(0);
    } finally {
      setRolePermissionsLoading(false);
    }
  }, []);

  const applyRolePermissionUpdate = useCallback(
    (payload: ApplyRolePermissionUpdatePayload) => {
      setRolePermissions((prev) => {
        if (!prev?.length) return prev;
        return prev.map((role) => updateRoleByPayload(role, payload));
      });
    },
    [],
  );

  useEffect(() => {
    refetchRolePermissions();
  }, [refetchRolePermissions]);

  const value = useMemo<RolesPermissionsSlice>(
    () => ({
      rolePermissions,
      rolePermissionsCount,
      rolePermissionsLoading,
      rolePermissionsError,
      refetchRolePermissions,
      applyRolePermissionUpdate,
    }),
    [
      rolePermissions,
      rolePermissionsCount,
      rolePermissionsLoading,
      rolePermissionsError,
      refetchRolePermissions,
      applyRolePermissionUpdate,
    ],
  );

  return (
    <RolesPermissionsContext.Provider value={value}>
      {children}
    </RolesPermissionsContext.Provider>
  );
}
